/**
 * Questo file definisce il componente React per la pagina "Flashcard della Community".
 * - Mostra un elenco di tutte le cartelle pubbliche condivise dagli utenti.
 * - Implementa una funzionalità di ricerca per filtrare le cartelle per nome o autore.
 * - Gestisce la paginazione per mostrare un numero limitato di cartelle per pagina.
 * - Fa il dispatch dell'azione `fetchPublicFolders` per caricare i dati dal backend.
 * - Fornisce link per avviare una sessione di studio o per visualizzare i dettagli di una cartella.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchPublicFolders } from '../redux/flashcardSlice';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import './PublicFolders.css';

// Costante per il numero di cartelle da visualizzare per pagina.
const FOLDERS_PER_PAGE = 12;

/**
 * COMPONENTE PRINCIPALE: PublicFolders
 */
export default function PublicFolders() {
    const dispatch = useDispatch();
    // Estrae i dati necessari (cartelle pubbliche, stato di caricamento, errori) da Redux.
    const { publicFolders, loading, error } = useSelector((state) => state.flashcards);

    // Stato per il termine di ricerca inserito dall'utente.
    const [searchTerm, setSearchTerm] = useState('');
    // Stato per la pagina corrente della paginazione.
    const [currentPage, setCurrentPage] = useState(1);

    // EFFETTO: Carica le cartelle pubbliche al montaggio del componente.
    useEffect(() => {
        dispatch(fetchPublicFolders());
    }, [dispatch]);

    /**
     * MEMOIZZAZIONE: Filtra le cartelle in base al termine di ricerca.
     * - `useMemo` ricalcola `filteredFolders` solo se `publicFolders` o `searchTerm` cambiano.
     * - Il filtro non è case-sensitive.
     * - Restituisce un array vuoto se `publicFolders` non è ancora stato caricato.
     */
    const filteredFolders = useMemo(() => {
        if (!publicFolders) return [];
        return publicFolders.filter(folder =>
            folder.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            folder.autore.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [publicFolders, searchTerm]);

    /**
     * MEMOIZZAZIONE: Applica la paginazione alle cartelle filtrate.
     * - `useMemo` ricalcola `paginatedFolders` solo se le cartelle filtrate o la pagina corrente cambiano.
     * - Utilizza `slice` per estrarre la porzione corretta di cartelle da visualizzare.
     */
    const paginatedFolders = useMemo(() => {
        const startIndex = (currentPage - 1) * FOLDERS_PER_PAGE;
        return filteredFolders.slice(startIndex, startIndex + FOLDERS_PER_PAGE);
    }, [filteredFolders, currentPage]);

    // Calcola il numero totale di pagine necessarie.
    const totalPages = Math.ceil(filteredFolders.length / FOLDERS_PER_PAGE);

    // Gestore per cambiare la pagina di visualizzazione.
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    return (
        <>
            {loading && <LoadingSpinner message="Caricamento delle cartelle pubbliche..." />}
            <Navbar />
            <div className="public-folders-container">
                <div className="container">
                    {/* Header della pagina */}
                    <div className="page-header text-center">
                        <h1 className="display-4 fw-bold">Flashcard della Community</h1>
                        <p className="lead text-muted">
                            Esplora i set di studio creati e condivisi da altri utenti come te.
                        </p>
                    </div>

                    {/* Barra di ricerca */}
                    <div className="row justify-content-center mb-4">
                        <div className="col-md-8">
                            <div className="input-group input-group-lg">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Cerca per nome della cartella o autore..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        // Resetta la paginazione alla prima pagina quando si esegue una nuova ricerca.
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Gestione degli errori */}
                    {error && <div className="alert alert-danger">{error}</div>}

                    {/* Stato vuoto: mostrato se non ci sono risultati o cartelle */}
                    {!loading && filteredFolders.length === 0 ? (
                        <div className="text-center p-5 empty-state">
                            <i className="bi bi-search-heart display-1 text-muted mb-3"></i>
                            <h4>Nessun risultato trovato</h4>
                            <p className="lead text-muted">
                                {searchTerm 
                                    ? "Prova a modificare i termini della tua ricerca." 
                                    : "Al momento non ci sono cartelle pubbliche disponibili. Torna a trovarci presto!"}
                            </p>
                        </div>
                    ) : (
                        // Griglia delle cartelle pubbliche
                        <div className="row g-4">
                            {paginatedFolders.map(folder => (
                                <div className="col-md-6 col-lg-4" key={folder.id}>
                                    <div className="public-folder-card h-100">
                                        <div className="card-body d-flex flex-column">
                                            <div className="card-content-top">
                                                <i className="bi bi-folder-symlink-fill card-icon"></i>
                                                <h5 className="card-title fw-bold">{folder.nome}</h5>
                                                <p className="card-subtitle mb-2 text-muted small">
                                                    Creato da: {folder.autore}
                                                </p>
                                            </div>
                                            <div className="mt-auto">
                                                <span className="badge bg-light text-dark rounded-pill mb-3">
                                                    {folder.numero_flashcard} flashcard
                                                </span>
                                                <Link 
                                                    to={`/public/study/${folder.id}`} 
                                                    // Disabilita il pulsante se la cartella è vuota.
                                                    className={`btn btn-primary w-100 ${folder.numero_flashcard === 0 ? 'disabled' : ''}`}
                                                >
                                                    <i className="bi bi-play-circle-fill me-2"></i>Avvia Studio
                                                </Link>
                                                <Link to={`/public/folder/${folder.id}`} className="btn btn-link btn-sm w-100 mt-2">
                                                    Visualizza Dettagli
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Paginazione: mostrata solo se c'è più di una pagina */}
                    {totalPages > 1 && (
                        <nav className="d-flex justify-content-center mt-5">
                            <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                                        Precedente
                                    </button>
                                </li>
                                {/* Genera i pulsanti per ogni pagina */}
                                {[...Array(totalPages).keys()].map(num => (
                                    <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => handlePageChange(num + 1)}>
                                            {num + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                                        Successivo
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </div>
            </div>
        </>
    );
}